import { BadgeWithIcon } from '@orn/shared-web/components/Badge';
import { RefreshIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { formatCurrencyWhole } from '../../../utils/formatting';
import Widget from '../../Widget';

import { VALUE_CLASS_NAME, VALUE_STEP_CLASS_NAMES, VALUE_STEPS } from './valueStep';
import ValueWithChange from './ValueWithChange';

import type { IValueChange } from '../../../utils/account/balanceHistory';

const SpinningRefreshIcon: React.FunctionComponent<{ className?: string }> = ({ className }) => (
  <RefreshIcon className={cx('animate-spin', className)} />
);

interface ICurrentValueCardProps {
  /** The account's latest recorded value; null before its first one arrives. */
  value: number | null;
  /** The move since an earlier value; omit or pass null to show the value alone. */
  change?: IValueChange | null;
  /** What `change` is measured from, e.g. "since yesterday". */
  changeLabel?: string;
  className?: string;
}

/**
 * "Current value" card for an account whose worth is a recorded snapshot. Shows the value in whole
 * dollars with an optional change beside it (amount, percent to two places, and its label). The
 * value steps down the display type scale until the pair shares one row; when no step fits, the
 * change goes under the value. Before the first value it shows a dash and an "Updating" badge.
 */
const CurrentValueCard = ({ value, change = null, changeLabel, className }: ICurrentValueCardProps) => (
  <Widget className={className}>
    <Widget.Header title="Current value" />
    <Widget.Body className="flex-1 justify-center p-5">
      {value !== null && change ? (
        <ValueWithChange formattedValue={formatCurrencyWhole(value)} change={change} changeLabel={changeLabel} />
      ) : (
        <div className="flex items-center justify-between gap-4">
          <div data-testid="current-value" className={cx(VALUE_CLASS_NAME, VALUE_STEP_CLASS_NAMES[VALUE_STEPS[0]])}>
            {value === null ? '—' : formatCurrencyWhole(value)}
          </div>
          {value === null && (
            <BadgeWithIcon size="lg" color="warning" iconLeading={SpinningRefreshIcon}>
              Updating
            </BadgeWithIcon>
          )}
        </div>
      )}
    </Widget.Body>
  </Widget>
);

export default CurrentValueCard;
